import type { PagesDto } from '@octopus-do/octopus-api-client'
import { parseNestError } from '@store/reducers/dashboard/utils'
import { useEffect, useState } from 'react'

import { NestClient } from '@/nest'
import { DEFAULT_SORT } from '../constants'
import type { SectionType, SortType } from '../types'

type PageItems = PagesDto['items']

interface Query {
  changes: boolean
  search: string
  section: SectionType
  sort: SortType
  // Bumped when a crawl lands: the report it describes is a different one, so
  // the list restarts rather than keeping rows that no longer exist.
  version?: number
}

// The API answers 50 rows at a time and says whether more exist, so the offset
// of the next page is simply how many rows are already on screen. Searching and
// sorting happen in the query too, so the browser never holds the whole list.
export const useReportPages = (uuid: string | undefined, query: Query) => {
  const { changes, search, section, sort, version } = query
  const [items, setItems] = useState<PageItems>([])
  const [hasMore, setHasMore] = useState(false)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchPages = (offset: number) => {
    if (!uuid) {
      return Promise.reject(new Error('No report'))
    }

    return NestClient.sitemapReport.pages(uuid, {
      ...(changes ? { changes: 'true' } : {}),
      offset: String(offset),
      ...(search ? { search } : {}),
      section,
      ...(sort === DEFAULT_SORT ? {} : { sort }),
    })
  }

  // Switching section, the changes filter, the search term or the sort starts a
  // new list rather than appending to the old one — paging restarts at the top —
  // and a slow answer to a view the user has already left is dropped instead of
  // overwriting the current one.
  useEffect(() => {
    if (!uuid) {
      return
    }

    let cancelled = false

    setLoading(true)

    fetchPages(0)
      .then((data) => {
        if (cancelled) {
          return
        }

        setItems(data.items)
        setHasMore(data.hasMore)
        setError(null)
      })
      .catch((cause) => {
        if (!cancelled) {
          setError(parseNestError(cause))
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [uuid, section, changes, search, sort, version])

  const loadMore = () => {
    if (loading || loadingMore || !hasMore) {
      return
    }

    setLoadingMore(true)

    fetchPages(items.length)
      .then((data) => {
        setItems((previous) => [...previous, ...data.items])
        setHasMore(data.hasMore)
      })
      .catch((cause) => setError(parseNestError(cause)))
      .finally(() => setLoadingMore(false))
  }

  return { error, hasMore, items, loading, loadingMore, loadMore }
}
